// AGC: Animated audio waveform dots — reads amplitude from AnalyserNode
import React, { useEffect, useRef } from 'react'

interface AudioWaveformProps {
  analyserNode: AnalyserNode | null
  isActive: boolean
  dotCount?: number
}

const DOT_COUNT_DEFAULT = 20
const MIN_DOT_HEIGHT = 3
const MAX_DOT_HEIGHT = 16

export const AudioWaveform: React.FC<AudioWaveformProps> = ({
  analyserNode,
  isActive,
  dotCount = DOT_COUNT_DEFAULT,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const animationRef = useRef<number>(0)
  const dataArrayRef = useRef<Uint8Array<ArrayBuffer> | null>(null)

  useEffect(() => {
    if (!analyserNode || !isActive || !canvasRef.current) {
      if (animationRef.current) cancelAnimationFrame(animationRef.current)
      return
    }

    const bufferLength = analyserNode.frequencyBinCount
    dataArrayRef.current = new Uint8Array(bufferLength)
    const canvas = canvasRef.current
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const draw = () => {
      if (!dataArrayRef.current || !analyserNode) return
      analyserNode.getByteFrequencyData(dataArrayRef.current)

      ctx.clearRect(0, 0, canvas.width, canvas.height)

      const barWidth = canvas.width / dotCount
      const samplesPerBar = Math.floor(bufferLength / dotCount)

      for (let i = 0; i < dotCount; i++) {
        let sum = 0
        for (let j = 0; j < samplesPerBar; j++) {
          sum += dataArrayRef.current[i * samplesPerBar + j] ?? 0
        }
        const avg = sum / samplesPerBar / 255

        const dotHeight = MIN_DOT_HEIGHT + avg * (MAX_DOT_HEIGHT - MIN_DOT_HEIGHT)
        const x = i * barWidth + barWidth / 2
        const y = canvas.height / 2

        ctx.beginPath()
        ctx.roundRect(x - 1.5, y - dotHeight / 2, 3, dotHeight, 1.5)
        ctx.fillStyle = '#22c55e'
        ctx.fill()
      }

      animationRef.current = requestAnimationFrame(draw)
    }

    draw()

    return () => {
      if (animationRef.current) cancelAnimationFrame(animationRef.current)
    }
  }, [analyserNode, isActive, dotCount])

  return (
    <canvas
      ref={canvasRef}
      width={200}
      height={24}
      className="flex-1"
    />
  )
}

export default AudioWaveform
